import api from "./axios";

export interface Attendance {
    id: string;
    class_subject_id: string;
    teacher_id: string;
    attendance_date: string;
    mode: "qr" | "manual";
    start_time: string;
    end_time: string;
    qr_code?: string | null;
    qr_expires_at?: string | null;
    notes?: string | null;
    is_locked: boolean;
    created_at: string;
    updated_at: string;

    // Relationships
    class_subject?: {
        id: string;
        class?: {
            id: string;
            name: string;
            grade_level: string;
        };
        subject?: {
            id: string;
            name: string;
            code: string;
        };
        semester?: {
            id: string;
            name: string;
        };
    };
    teacher?: {
        id: string;
        nip: string;
        user?: {
            id: string;
            name: string;
        };
    };

    // Computed attributes
    present_count?: number;
    absent_count?: number;
    late_count?: number;
    excused_count?: number;
    total_count?: number;
    attendance_percentage?: number;
    mode_text?: string;
}

export interface AttendanceLog {
    id: string;
    attendance_id: string;
    student_id: string;
    status: "present" | "absent" | "late" | "excused";
    check_in_time?: string | null;
    notes?: string | null;
    marked_by?: string | null;
    created_at: string;
    updated_at: string;

    // Relationships
    student?: {
        id: string;
        nisn: string;
        user?: {
            id: string;
            name: string;
        };
    };
    marked_by_teacher?: {
        id: string;
        user?: {
            id: string;
            name: string;
        };
    };
    attendance?: Attendance;

    // Computed attributes
    status_text?: string;
    status_color?: string;
}

export interface AttendanceFilters {
    class_subject_id?: string | null;
    class_id?: string | null;
    mode?: string | null;
    teacher_id?: string | null;
    date_from?: string | null;
    date_to?: string | null;
    page?: number;
    per_page?: number;
}

export interface CreateAttendanceData {
    class_subject_id: string;
    teacher_id?: string;
    attendance_date: string;
    mode: "qr" | "manual";
    start_time: string;
    end_time: string;
    notes?: string;
}

export interface UpdateAttendanceData {
    attendance_date?: string;
    mode?: "qr" | "manual";
    start_time?: string;
    end_time?: string;
    notes?: string;
}

export interface MarkAttendanceData {
    student_id: string;
    status: "present" | "absent" | "late" | "excused";
    notes?: string;
}

export interface RosterRow {
    student: {
        id: string;
        name?: string;
        nisn?: string;
    };
    log: {
        id: string;
        status: "present" | "absent" | "late" | "excused";
        check_in_time?: string | null;
        notes?: string | null;
    } | null;
}

export interface RecapStudentRow {
    student: {
        id: string;
        name?: string;
        nisn?: string;
    };
    present_count: number;
    absent_count: number;
    late_count: number;
    excused_count: number;
    logged_count: number;
    attendance_percentage: number;
}

export interface AttendanceRecap {
    class_subject: {
        id: string;
        class?: { id: string; name: string } | null;
        subject?: { id: string; name: string } | null;
        semester?: { id: string; name: string } | null;
    };
    total_sessions: number;
    students: RecapStudentRow[];
}

const attendanceApi = {
    async getAll(filters: AttendanceFilters = {}): Promise<{
        success: boolean;
        message: string;
        data: { items: Attendance[]; meta: any };
    }> {
        const response = await api.get("/v1/attendance", { params: filters });
        return response.data;
    },

    async getById(id: string): Promise<Attendance> {
        const response = await api.get(`/v1/attendance/${id}`);
        return response.data.data;
    },

    async create(data: CreateAttendanceData): Promise<Attendance> {
        const response = await api.post("/v1/attendance", data);
        return response.data.data;
    },

    async update(id: string, data: UpdateAttendanceData): Promise<Attendance> {
        const response = await api.put(`/v1/attendance/${id}`, data);
        return response.data.data;
    },

    async delete(id: string): Promise<void> {
        await api.delete(`/v1/attendance/${id}`);
    },

    async regenerateQr(id: string): Promise<Attendance> {
        const response = await api.post(`/v1/attendance/${id}/qr/regenerate`);
        return response.data.data;
    },

    async toggleLock(id: string): Promise<Attendance> {
        const response = await api.post(`/v1/attendance/${id}/lock`);
        return response.data.data;
    },

    async getLogs(
        id: string,
        filters: {
            status?: string | null;
            page?: number;
            per_page?: number;
        } = {},
    ): Promise<{
        success: boolean;
        message: string;
        data: { items: AttendanceLog[]; meta: any };
    }> {
        const response = await api.get(`/v1/attendance/${id}/logs`, {
            params: filters,
        });
        return response.data;
    },

    async getRoster(id: string): Promise<RosterRow[]> {
        const response = await api.get(`/v1/attendance/${id}/roster`);
        return response.data.data;
    },

    async markLog(
        id: string,
        data: MarkAttendanceData,
    ): Promise<AttendanceLog> {
        const response = await api.post(`/v1/attendance/${id}/logs`, data);
        return response.data.data;
    },

    async bulkMarkLogs(
        id: string,
        entries: MarkAttendanceData[],
    ): Promise<{ count: number }> {
        const response = await api.post(`/v1/attendance/${id}/logs/bulk`, {
            entries,
        });
        return response.data.data;
    },

    async updateLog(
        logId: string,
        data: { status: MarkAttendanceData["status"]; notes?: string },
    ): Promise<AttendanceLog> {
        const response = await api.put(`/v1/attendance/logs/${logId}`, data);
        return response.data.data;
    },

    async checkIn(code: string): Promise<AttendanceLog> {
        const response = await api.post("/v1/attendance/check-in", { code });
        return response.data.data;
    },

    async getMy(
        filters: {
            status?: string | null;
            date_from?: string | null;
            date_to?: string | null;
            page?: number;
            per_page?: number;
        } = {},
    ): Promise<{
        success: boolean;
        message: string;
        data: { items: AttendanceLog[]; meta: any };
    }> {
        const response = await api.get("/v1/attendance/my", {
            params: filters,
        });
        return response.data;
    },

    async getRecap(
        classSubjectId: string,
        filters: { date_from?: string | null; date_to?: string | null } = {},
    ): Promise<AttendanceRecap> {
        const response = await api.get("/v1/attendance/recap", {
            params: { class_subject_id: classSubjectId, ...filters },
        });
        return response.data.data;
    },
};

export default attendanceApi;
